import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { finalize } from "rxjs/operators";
import { AsistenciaConfig } from "src/app/shared/model/asistencia/asistencia.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { AsistenciaService } from "../asistencia.service";
import { horaParaCampo, horaParaServidor, motivoDelRechazo } from "./parametros.util";

const NO_SE_CONSULTARON = "No se pudieron consultar los parámetros.";
const NO_SE_GUARDARON = "No se pudieron guardar los parámetros.";

/**
 * Los parámetros con los que esta empresa evalúa su asistencia.
 *
 * Cada campo de esta pantalla es una decisión de negocio que se tomó
 * expresamente configurable, no un ajuste técnico: cuál de las tres
 * reglas de falta se usa, a qué hora se declara, si el mixto del mismo
 * día se declara o se infiere, cuánto desfase se acepta de una checada
 * encolada sin red, y desde cuándo se empieza a evaluar.
 *
 * Por eso cada uno lleva su explicación en la pantalla: quien la abre
 * dentro de seis meses no tiene por qué recordar qué significa
 * «acumulación de retardos».
 */
@Component({
  selector: "app-parametros",
  standalone: true,
  templateUrl: "./parametros.component.html",
  styleUrls: ["./parametros.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
})
export class ParametrosComponent implements OnInit {
  config: AsistenciaConfig = new AsistenciaConfig();
  loading = false;
  guardando = false;

  /**
   * Por qué no llegaron los parámetros. Mientras esté puesto no se pinta el
   * formulario ni se deja guardar: lo que habría en pantalla serían los
   * valores por omisión, y guardarlos pisaría la configuración real.
   */
  errorCarga: string | null = null;

  /**
   * Por qué el servidor rechazó el último guardado. Se pinta en rojo y lo
   * capturado se queda como estaba, para corregirlo sin volver a teclear.
   */
  errorGuardado: string | null = null;

  readonly zonas = [
    "America/Mexico_City",
    "America/Cancun",
    "America/Chihuahua",
    "America/Hermosillo",
    "America/Tijuana",
    "America/Mazatlan",
    "America/Merida",
    "America/Monterrey",
  ];

  constructor(
    private asistenciaService: AsistenciaService,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    queueMicrotask(() => {
      this.consultar();
      this.cdr.detectChanges();
    });
  }

  consultar(): void {
    this.loading = true;
    this.errorCarga = null;
    this.errorGuardado = null;
    this.cdr.detectChanges();

    this.asistenciaService
      .consultarConfig()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => {
          // Un cuerpo vacío sin sello (un proxy, un servidor viejo) tampoco
          // son los parámetros de la empresa.
          if (!respuesta) {
            this.errorCarga = NO_SE_CONSULTARON;
            return;
          }
          this.config = this.paraPantalla(respuesta);
        },
        error: (e) => {
          this.errorCarga = motivoDelRechazo(e, NO_SE_CONSULTARON);
        },
      });
  }

  guardar(): void {
    if (this.guardando || this.errorCarga) {
      return;
    }
    this.guardando = true;
    this.errorGuardado = null;
    this.cdr.detectChanges();

    this.asistenciaService
      .guardarConfig(this.paraServidor(this.config))
      .pipe(
        finalize(() => {
          this.guardando = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => {
          if (!respuesta) {
            this.errorGuardado = NO_SE_GUARDARON;
            return;
          }
          this.config = this.paraPantalla(respuesta);
          this.msg.successCall("Los parámetros quedaron guardados.", null, null);
        },
        // El rechazo llega sellado en cabeceras y sinSelloDeError lo trae
        // aquí con el motivo del servidor. Antes se miraba si el cuerpo
        // traía reglaFalta, pero el bean de error la trae por omisión: la
        // pantalla decía «guardados» y luego pintaba los valores de fábrica
        // encima de lo capturado. Aquí this.config no se toca.
        error: (e) => {
          this.errorGuardado = motivoDelRechazo(e, NO_SE_GUARDARON);
        },
      });
  }

  /** Lo que llega del servidor, con la hora como la quiere el campo. */
  private paraPantalla(respuesta: AsistenciaConfig): AsistenciaConfig {
    return Object.assign(new AsistenciaConfig(), respuesta, {
      horaCorteFalta: horaParaCampo(respuesta.horaCorteFalta),
    });
  }

  /**
   * Una copia para el servidor, con la hora completa. Copia y no el mismo
   * objeto: si el guardado se rechaza, lo que está en pantalla no cambia.
   */
  private paraServidor(config: AsistenciaConfig): AsistenciaConfig {
    return Object.assign(new AsistenciaConfig(), config, {
      horaCorteFalta: horaParaServidor(config.horaCorteFalta),
    });
  }

  get usaUmbral(): boolean {
    return this.config.reglaFalta === "UMBRAL_MINUTOS";
  }

  get usaAcumulacion(): boolean {
    return this.config.reglaFalta === "ACUMULACION_RETARDOS";
  }

  /** Lo que significa desactivar la hora del teléfono, dicho en la pantalla. */
  get desfaseDesactivado(): boolean {
    return this.config.minutosMaxDesfaseOffline === 0;
  }
}
